import { DecimalPipe, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import localePtBR from '@angular/common/locales/pt';
import { ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AppVersion } from '@ionic-native/app-version';
import { Camera } from '@ionic-native/camera';
import { Device } from '@ionic-native/device';
import { Globalization } from '@ionic-native/globalization';
import { HTTP } from '@ionic-native/http';
import { InAppBrowser } from '@ionic-native/in-app-browser';
import { LaunchNavigator } from '@ionic-native/launch-navigator';
import { Market } from '@ionic-native/market';
import { Network } from '@ionic-native/network';
import { SafariViewController } from '@ionic-native/safari-view-controller';
import { SocialSharing } from '@ionic-native/social-sharing';
import { SplashScreen } from '@ionic-native/splash-screen';
import { StatusBar } from '@ionic-native/status-bar';
import { IonicStorageModule } from '@ionic/storage';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { StompRService } from '@stomp/ng2-stompjs';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { IonicApp, IonicErrorHandler, IonicModule } from 'ionic-angular';
import { IonicImageViewerModule } from 'ionic-img-viewer';
import { NgCalendarModule } from 'ionic2-calendar';
import { FastgetApp } from './app.component';
import { APP_CONFIG } from './app.config';
import { BankAccountService } from './pages/bank-account/bank-account.service';
import { CallAttendantService } from './pages/call-attendant/call-attendant.service';
import { CategoryService } from './pages/category/category.service';
import { ProductService } from './pages/category/products/product.service';
import { CouponsService } from './pages/coupons/coupons.service';
import { CustomerService } from './pages/customers/customers.service';
import { MapService } from './pages/map/map.service';
import { OrderReservationService } from './pages/orders/order-reservation-config/order-reservation.service';
import { DeliveryService } from './pages/orders/orders-config/modal-delivery/delivery.service';
import { OrderService } from './pages/orders/orders.service';
import { PasswordService } from './pages/passwords/passwords.service';
import { PlaceService } from './pages/place/place.service';
import { QueueService } from './pages/queue/queue.service';
import { RatingService } from './pages/rating/rating.service';
import { ReceivableService } from './pages/receivables/legacy/receivables.service';
import { PaymentAccountService } from './pages/receivables/payment-account/payment-account.service';
import { ReservationService } from './pages/reservation/reservation.service';
import { StatisticsService } from './pages/statistics/statistics.service';
import { StoreService } from './pages/store/store.service';
import { TablePaymentService } from './pages/tables-payment/tables-payment.service';
import { TableService } from './pages/tables/table.service';
import { ToolsService } from './pages/tools/tools.service';
import { UserPageService } from './pages/user/user-page.service';
import { WalletService } from './pages/wallet/wallet.service';
import { DEFAULT_TIMEOUT, TimeoutInterceptor } from './shared/helpers/timeout-interceptor';
import { SharedModule } from './shared/modules/shared.module';
import { AnalyticsService } from './shared/services/analytics.service';
import { AppStateService } from './shared/services/app-state.service';
import { ConnectivityService } from './shared/services/connectivity.service';
import { PollingService } from './shared/services/polling.service';
import { ChartService } from './shared/services/chart.service';
import { IosSwipeBackService } from './shared/services/ios-swipe-back.service';
import { LanguageService } from './shared/services/language.service';
import { PaymentService } from './shared/services/payment.service';
import { PhotoService } from './shared/services/photo.service';
import { RequestService } from './shared/services/request.service';
import { SignupOriginService } from './shared/services/signup-origin.service';
import { SubscriptionService } from './shared/services/subscription.service';
import { UserService } from './shared/services/user.service';
import { WebSocketService } from './shared/services/websocket.service';

//https://angular.io/guide/i18n#i18n-pipes
registerLocaleData(localePtBR);

// export function HttpLoaderFactory(http: HttpClient) {
// 	return new TranslateHttpLoader(http);
// }
export function createTranslateLoader(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json?version=' + APP_CONFIG.APP_VERSION);
}

@NgModule({
  declarations: [FastgetApp],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    IonicImageViewerModule,
    IonicStorageModule.forRoot({
      name: 'app_DB',
    }),
    IonicModule.forRoot(FastgetApp, {
      backButtonText: '',
      swipeBackEnabled: false,
      preloadModules: true,
      mode: 'md',
      platforms: {
        ios: {
          statusbarPadding: true,
        },
      },
    }),
    NgxChartsModule,
    NgCalendarModule,
    SharedModule,
    ServiceWorkerModule.register('/ngsw-worker.js', {
      enabled: !(window as any).cordova && location.hostname != 'localhost',
    }), // && ENV.mode == "PROD"
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
  ],
  bootstrap: [IonicApp],
  entryComponents: [FastgetApp],
  providers: [
    AnalyticsService,
    AppStateService,
    AppVersion,
    BankAccountService,
    CallAttendantService,
    CategoryService,
    Camera,
    ChartService,
    ConnectivityService,
    CouponsService,
    CustomerService,
    DecimalPipe,
    DeliveryService,
    Device,
    StoreService,
    Globalization,
    HTTP,
    InAppBrowser,
    IosSwipeBackService,
    LanguageService,
    LaunchNavigator,
    MapService,
    Market,
    Network,
    OrderService,
    OrderReservationService,
    PasswordService,
    PaymentService,
    PaymentAccountService,
    PlaceService,
    PollingService,
    PhotoService,
    ProductService,
    QueueService,
    RatingService,
    ReceivableService,
    ReservationService,
    RequestService,
    SignupOriginService,
    SafariViewController,
    SocialSharing,
    SplashScreen,
    StatisticsService,
    StatusBar,
    StompRService,
    SubscriptionService,
    TableService,
    TablePaymentService,
    ToolsService,
    UserPageService,
    UserService,
    WalletService,
    WebSocketService,
    { provide: ErrorHandler, useClass: IonicErrorHandler },
    { provide: LOCALE_ID, useValue: 'pt' },
    { provide: HTTP_INTERCEPTORS, useClass: TimeoutInterceptor, multi: true },
    { provide: DEFAULT_TIMEOUT, useValue: 30000 },
  ],
})
export class AppModule {}



// WEBPACK FOOTER //
// ./src/app/app.module.ts